import { registerStyles } from "../utils/registerStyles";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { addDoc, collection, deleteDoc, doc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, startAfter } from "firebase/firestore";
import { CheckCheck, MessageSquare, Send } from "lucide-react";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import LoadingIndicator from "../components/LoadingIndicator";

export default function NoticeboardPage() {
  const { activeCompany } = useCompany();
  const { currentUser } = useAuth();
  return <Noticeboard key={`${activeCompany?.companyAccountUid}:${currentUser?.uid}`} company={activeCompany} user={currentUser} />;
}

function Noticeboard({ company, user }) {
  const companyId = company?.companyAccountUid;
  const isAdmin = ["admin", "super-admin", "superadmin"].includes(company?.role);
  const [notices, setNotices] = useState([]);
  const [olderNotices, setOlderNotices] = useState([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const [olderError, setOlderError] = useState("");
  const oldest = useRef(null);
  const latestPage = useRef([]);
  const loadingPage = useRef(false);
  const messages = useRef(null);
  const scrollPosition = useRef(null);
  const initialScroll = useRef(false);
  const mounted = useRef(true);
  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(Boolean(companyId));
  const [error, setError] = useState("");
  const [readError, setReadError] = useState("");
  const [readersStatus, setReadersStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const end = useRef(null);
  const busy = useRef(false);
  const employee = company?.employedData;
  const readerName = [employee?.firstName, employee?.lastName].filter(Boolean).join(" ") || employee?.name || user?.displayName || "Administratör";
  const allNotices = [...new Map([...olderNotices, ...notices].map(notice => [notice.id, notice])).values()]
    .sort((a, b) => (a.createdAt?.toMillis() ?? Infinity) - (b.createdAt?.toMillis() ?? Infinity) || a.id.localeCompare(b.id));

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(query(collection(db, "users", companyId, "announcements"), orderBy("createdAt", "desc"), limit(20)), snapshot => {
      const next = snapshot.docs.map(item => ({ ...item.data(), id: item.id, snapshot: item }));
      const nextIds = new Set(next.map(item => item.id));
      const previousIds = new Set(latestPage.current.map(item => item.id));
      if (snapshot.size === 20 && next.some(item => !previousIds.has(item.id))) {
        const displaced = latestPage.current.filter(item => !nextIds.has(item.id) &&
          item.createdAt?.toMillis() <= next[next.length - 1]?.createdAt?.toMillis());
        if (displaced.length) setOlderNotices(previous => [...previous, ...displaced]);
      }
      latestPage.current = next;
      if (!loadingPage.current && !oldest.current) {
        setHasOlder(snapshot.size === 20);
      }
      setNotices(next);
      setLoading(false);
      setError("");
    }, () => { setLoading(false); setError("Kunde inte ladda meddelandena. Öppna sidan igen för att försöka på nytt."); });
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !isAdmin) return;
    return onSnapshot(collection(db, "users", companyId, "announcementReads"), snapshot => {
      setReaders(snapshot.docs.map(item => ({ ...item.data(), id: item.id })));
      setReadersStatus("ready");
    }, () => setReadersStatus("error"));
  }, [companyId, isAdmin]);

  useEffect(() => {
    if (!companyId || !user?.uid || loading || error) return;
    let active = true;
    const markRead = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        await setDoc(doc(db, "users", companyId, "announcementReads", user.uid), {
          readerName, email: user.email || "", lastReadAt: serverTimestamp(),
        }, { merge: true });
        if (active) setReadError("");
      } catch {
        if (active) setReadError("Kunde inte markera meddelandena som lästa.");
      }
    };
    markRead();
    document.addEventListener("visibilitychange", markRead);
    return () => { active = false; document.removeEventListener("visibilitychange", markRead); };
  }, [companyId, user?.uid, user?.email, readerName, loading, error, notices]);

  useLayoutEffect(() => {
    const container = messages.current;
    if (!container || loading) return;
    if (scrollPosition.current) {
      const { height, top } = scrollPosition.current;
      container.scrollTop = top + container.scrollHeight - height;
      scrollPosition.current = null;
    } else if (!initialScroll.current) {
      container.scrollTop = container.scrollHeight;
      initialScroll.current = true;
    }
  }, [olderNotices, loading]);

  useEffect(() => {
    const container = messages.current;
    if (container && container.scrollHeight - container.scrollTop - container.clientHeight < 160) {
      end.current?.scrollIntoView?.({ block: "nearest" });
    }
  }, [notices]);

  const loadOlder = async () => {
    const cursor = oldest.current || notices[notices.length - 1]?.snapshot;
    if (!companyId || !cursor || !hasOlder || loadingPage.current || loading) return;
    loadingPage.current = true;
    setLoadingOlder(true);
    setOlderError("");
    try {
      const snapshot = await getDocs(query(collection(db, "users", companyId, "announcements"),
        orderBy("createdAt", "desc"), startAfter(cursor), limit(20)));
      if (!mounted.current) return;
      const container = messages.current;
      if (container) scrollPosition.current = { height: container.scrollHeight, top: container.scrollTop };
      oldest.current = snapshot.docs[snapshot.docs.length - 1] || cursor;
      setOlderNotices(previous => [...previous, ...snapshot.docs.map(item => ({ ...item.data(), id: item.id }))]);
      setHasOlder(snapshot.size === 20);
    } catch {
      if (mounted.current) setOlderError("Kunde inte ladda äldre meddelanden. Försök igen.");
    } finally {
      loadingPage.current = false;
      if (mounted.current) setLoadingOlder(false);
    }
  };

  const publish = async event => {
    event.preventDefault();
    if (!companyId || !user || !isAdmin || !message.trim() || busy.current) return;
    busy.current = true;
    setSaving(true);
    setError("");
    try {
      await addDoc(collection(db, "users", companyId, "announcements"), {
        message: message.trim(), authorId: user.uid,
        authorName: user.displayName || "Administratör", createdAt: serverTimestamp(),
      });
      setMessage("");
    } catch { setError("Kunde inte publicera. Försök igen. Meddelandet har inte publicerats."); }
    finally { busy.current = false; setSaving(false); }
  };

  const remove = async id => {
    const notice = allNotices.find(item => item.id === id);
    if (!companyId || !user?.uid || !isAdmin || notice?.authorId !== user.uid || busy.current) return;
    if (!window.confirm("Radera meddelande? Meddelandet tas bort från företagets anslagstavla för alla användare.")) return;
    busy.current = true;
    setDeleting(id);
    try {
      await deleteDoc(doc(db, "users", companyId, "announcements", id));
      setOlderNotices(previous => previous.filter(item => item.id !== id));
    }
    catch { setError("Kunde inte radera meddelandet. Försök igen."); }
    finally { busy.current = false; setDeleting(null); }
  };

  return <div className="noticeboard-page">
    <div className="noticeboard-company">
      <p>{companyId ? <>Visar anslagstavla för: <strong>{company.companyName}</strong></> : "Välj ett företag för att se meddelanden."}</p>
    </div>
    <section className="noticeboard" aria-labelledby="noticeboard-title">
    <header className="noticeboard-header"><h1 id="noticeboard-title">Anslagstavla</h1></header>
    {loading && <LoadingIndicator message="Laddar meddelanden…" />}
    {error && <p className="noticeboard-error" role="alert">{error}</p>}
    {readError && <p className="noticeboard-error" role="alert">{readError}</p>}
    {isAdmin && readersStatus === "error" && <p className="noticeboard-error" role="alert">Kunde inte ladda vilka som har läst meddelandena.</p>}
    <div ref={messages} className="noticeboard-messages" role="region" aria-label="Företagets meddelanden" tabIndex={0}
      onScroll={event => { if (event.currentTarget.scrollTop < 80) loadOlder(); }}>
      {hasOlder && <button type="button" onClick={loadOlder} disabled={loadingOlder}>{loadingOlder ? "Laddar äldre meddelanden…" : "Visa äldre meddelanden"}</button>}
      {olderError && <p className="noticeboard-error" role="alert">{olderError}</p>}
      {companyId && !loading && !error && notices.length === 0 && <div className="noticeboard-empty"><MessageSquare size={40} /><p>Det finns inga meddelanden ännu.</p></div>}
      {allNotices.map(notice => {
        const own = notice.authorId === user?.uid;
        const seenBy = readers.filter(reader => reader.readerName?.trim() && !reader.readerName.includes("@") && notice.createdAt && reader.lastReadAt?.toMillis() >= notice.createdAt.toMillis()).sort((a, b) => a.readerName.localeCompare(b.readerName, "sv"));
        return <article key={notice.id} className={`noticeboard-message${own ? " noticeboard-own" : ""}`}>
          <span className="noticeboard-author">{notice.authorName}</span>
          <div className="noticeboard-bubble">{notice.message}</div>
          <div className="noticeboard-meta"><time>{notice.createdAt ? notice.createdAt.toDate().toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }) : "Skickar…"}</time>
            {isAdmin && own && <button type="button" className="noticeboard-delete" disabled={Boolean(deleting) || saving} onClick={() => remove(notice.id)}>{deleting === notice.id ? "Raderar…" : "Radera"}</button>}
          </div>
          {isAdmin && readersStatus === "loading" && <small>Laddar läskvitton…</small>}
          {isAdmin && readersStatus === "ready" && <div className="noticeboard-readers"><button type="button" aria-expanded={expanded === notice.id} onClick={() => setExpanded(expanded === notice.id ? null : notice.id)}><CheckCheck size={16} />Sett av {seenBy.length}</button>
            {expanded === notice.id && <ul>{seenBy.map(reader => <li key={reader.id}>{reader.readerName.trim()}</li>)}</ul>}
          </div>}
        </article>;
      })}
      <div ref={end} />
    </div>
    {companyId && isAdmin && <form className="noticeboard-composer" onSubmit={publish}>
      <textarea aria-label="Meddelande till företaget" placeholder="Skriv ett meddelande…" maxLength={5000} rows={3} value={message} onChange={event => setMessage(event.target.value)} disabled={saving} />
      <button type="submit" disabled={saving || Boolean(deleting) || !message.trim()} aria-label={saving ? "Skickar meddelande" : "Skicka meddelande"}><Send size={20} /><span>{saving ? "Skickar…" : "Skicka"}</span></button>
    </form>}
    </section>
  </div>;
}

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("NoticeboardPage", `
.noticeboard-company { margin-bottom: 16px; padding: 0 0 8px; display: flex; justify-content: space-between; align-items: center; }
.noticeboard-company p { color: #6b7280; font-size: 14px; margin: 0; }
.noticeboard { display: flex; flex-direction: column; height: calc(100dvh - 108px); min-height: 420px; max-width: 1100px; margin: 0 auto; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 20px; overflow: hidden; color: #0f172a; }
.noticeboard-header { padding: 24px; background: white; border-bottom: 1px solid #e2e8f0; }
.noticeboard-header h1 { margin: 0; font-size: 26px; }
.noticeboard-messages { flex: 1; min-height: 0; overflow-y: auto; padding: 24px; }
.noticeboard-empty { padding: 40px 0; text-align: center; color: #64748b; }
.noticeboard-message { display: flex; flex-direction: column; align-items: flex-start; max-width: 800px; margin: 16px auto; gap: 5px; }
.noticeboard-own { align-items: flex-end; }
.noticeboard-author, .noticeboard-meta { color: #64748b; font-size: 12px; padding: 0 10px; }
.noticeboard-bubble { max-width: 88%; box-sizing: border-box; padding: 12px 16px; border-radius: 20px 20px 20px 5px; background: #e2e8f0; white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.5; }
.noticeboard-own .noticeboard-bubble { background: #6366f1; color: white; border-radius: 20px 20px 5px 20px; }
.noticeboard-meta { display: flex; align-items: center; gap: 12px; }
.noticeboard button { cursor: pointer; font: inherit; }
.noticeboard button:disabled { opacity: .45; cursor: not-allowed; }
.noticeboard button:focus-visible, .noticeboard textarea:focus-visible { outline: 3px solid #818cf8; outline-offset: 3px; }
.noticeboard-delete { border: 0; background: transparent; color: #b42318; padding: 10px 4px; }
.noticeboard-readers { max-width: 88%; padding: 0 10px; color: #64748b; font-size: 12px; }
.noticeboard-readers button { display: flex; align-items: center; gap: 6px; border: 0; background: transparent; color: inherit; min-height: 36px; }
.noticeboard-readers ul { list-style: none; padding: 0; margin: 0; color: #0f172a; overflow-wrap: anywhere; }
.noticeboard-readers li { margin-bottom: 5px; }
.noticeboard-error { margin: 8px 24px; color: #b42318; }
.noticeboard-composer { display: flex; align-items: flex-end; gap: 12px; padding: 20px 24px; border-top: 1px solid #e2e8f0; background: white; }
.noticeboard-composer textarea { flex: 1; min-width: 0; max-height: 180px; resize: vertical; box-sizing: border-box; border: 1px solid #e2e8f0; border-radius: 16px; padding: 14px; background: #f1f5f9; color: #0f172a; font: inherit; }
.noticeboard-composer button { display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 46px; padding: 12px 18px; border: 0; border-radius: 24px; background: #6366f1; color: white; }
@media (max-width: 600px) { .noticeboard { height: calc(100dvh - 144px); border-radius: 12px; } .noticeboard-header, .noticeboard-messages, .noticeboard-composer { padding: 16px; } .noticeboard-composer button span { display: none; } }
`);
